import { useState, useEffect, ReactNode } from "react";
import { ChevronDown, ChevronRight, Eye, EyeOff, CheckSquare, Square, BookOpen, Menu, X, ArrowUp, RotateCcw, Shuffle, Layers } from "lucide-react";

// ─── Type Definitions ────────────────────────────────────────────────────────

interface FlashcardData {
  term: string;
  definition: string;
}

interface FlashcardGroup {
  topic: string;
  color: keyof typeof colorMap;
  cards: FlashcardData[];
}

interface FlashcardWithTopic extends FlashcardData {
  topic: string;
  color: keyof typeof colorMap;
}

// ─── Data ────────────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: "number-systems", title: "1.2 Number Systems (Dec, Bin, Hex)", num: 1 },
  { id: "conversions", title: "1.2 Conversions Between Bases", num: 2 },
  { id: "word-size", title: "1.3 Word Size & Combinations", num: 3 },
  { id: "data-storage", title: "1.4 Data Storage Units", num: 4 },
  { id: "integers", title: "1.5 Representing Integers", num: 5 },
  { id: "characters", title: "1.6 Character Representation", num: 6 },
  { id: "screen-data", title: "1.7 Screen Data, RGB & Resolution", num: 7 },
  { id: "hw-sw", title: "2.1 Hardware and Software", num: 8 },
  { id: "ipc", title: "2.2 General Processing Cycle & ICT", num: 9 },
  { id: "data-info", title: "2.3 Data and Information", num: 10 },
  { id: "comp-types", title: "2.4 Computer System Types", num: 11 },
  { id: "os", title: "2.5 Operating Systems", num: 12 },
  { id: "app-sw", title: "2.6 Application Software", num: 13 },
  { id: "cpu", title: "3.1 The CPU", num: 14 },
  { id: "memory", title: "3.2 Primary Memory", num: 15 },
  { id: "storage", title: "3.3 Secondary Storage", num: 16 },
  { id: "input", title: "3.4 Input Devices", num: 17 },
  { id: "output", title: "3.5 Output Devices & Ports", num: 18 },
  { id: "sys-sw", title: "4.1 System Software", num: 19 },
  { id: "licensing", title: "4.2 Software Licensing", num: 20 },
  { id: "exercises", title: "Practice Exercises", num: 21 },
];

// ─── LU grouping (sidebar nav) ──────────────────────────────────────────────

function getLU(title: string): number | null {
  const m = title.match(/^(\d+)\./) || title.match(/^LU(\d+)/i);
  return m ? Number(m[1]) : null;
}

const LU_TITLES: Record<number, string> = {
  1: "Data Representation",
  2: "Software & Systems",
  3: "Hardware",
  4: "System Software",
  0: "Practice",  // catch-all for sections without an LU prefix (e.g. mixed exercises)
};

type SectionItem = typeof SECTIONS[number];

const SECTIONS_BY_LU: Record<number, SectionItem[]> = SECTIONS.reduce((acc, s) => {
  const lu = getLU(s.title) ?? 0;
  (acc[lu] ||= []).push(s);
  return acc;
}, {} as Record<number, SectionItem[]>);

// Sort LU numbers ascending; the catch-all "0" group is rendered last.
const LU_NUMBERS: number[] = Object.keys(SECTIONS_BY_LU)
  .map(Number)
  .sort((a, b) => (a || 99) - (b || 99));

const FLASHCARDS: FlashcardGroup[] = [
  { topic: "Number Systems", color: "sky", cards: [
    { term: "Binary (Base-2)", definition: "A number system using only 0s and 1s. The foundation of all computing — computers are two-state machines." },
    { term: "Decimal (Base-10)", definition: "The everyday number system using digits 0–9. Column values are powers of 10 (1, 10, 100, 1000…)." },
    { term: "Hexadecimal (Base-16)", definition: "A number system using digits 0–9 and letters A–F (where A=10, B=11… F=15). Used as shorthand for binary." },
    { term: "Bit", definition: "Binary digit — the smallest unit of data in computing. Either a 0 or a 1." },
    { term: "Byte", definition: "A group of 8 bits. Can represent 2⁸ = 256 different values (0–255)." },
    { term: "4 bits = 1 hex digit", definition: "Since 16 = 2⁴, every group of 4 binary digits maps to exactly one hexadecimal digit. E.g. 1101₂ = D₁₆." },
  ]},
  { topic: "Data Representation", color: "violet", cards: [
    { term: "Word Size", definition: "The number of bits a CPU can process at one time. Current standard: 32 or 64 bits." },
    { term: "2ⁿ Formula", definition: "The number of possible combinations in n bits = 2ⁿ. E.g. 8 bits → 2⁸ = 256 combinations." },
    { term: "Unsigned Integer", definition: "A whole number that can only be positive (or zero). All bits represent value. Range: 0 to 2ⁿ − 1." },
    { term: "Signed Integer", definition: "A whole number that can be positive or negative. First bit = sign. Range: −2ⁿ⁻¹ to 2ⁿ⁻¹ − 1." },
    { term: "Kilobyte (KB)", definition: "1024 bytes = 2¹⁰ bytes. Note: NOT 1000 bytes (though often approximated as such)." },
    { term: "Megabyte (MB)", definition: "1024 KB = 2²⁰ bytes ≈ 1 million bytes." },
    { term: "Gigabyte (GB)", definition: "1024 MB = 2³⁰ bytes ≈ 1 billion bytes." },
    { term: "Terabyte (TB)", definition: "1024 GB = 2⁴⁰ bytes ≈ 1 trillion bytes." },
  ]},
  { topic: "Characters & Screen", color: "amber", cards: [
    { term: "ASCII", definition: "American Standard Code for Information Interchange. Early 8-bit character set with 256 characters. Limited — couldn't support all languages." },
    { term: "Unicode", definition: "A universal standard providing a unique number for every character in every language. Replaced ASCII's limitations." },
    { term: "UTF-8", definition: "Unicode Transformation Format — 8-bit. Uses 1–4 bytes per character. An 'octet' is one group of 8 bits." },
    { term: "Pixel", definition: "A tiny dot on screen — the smallest element of a digital image. Each pixel has its own colour." },
    { term: "RGB", definition: "Red Green Blue — system of producing any colour by mixing 3 bytes (0–255 each). 3 bytes per pixel = 2²⁴ = 16,777,216 colours." },
    { term: "Refresh Rate", definition: "How many times per second the screen redraws, measured in Hertz (Hz). E.g. 60Hz = 60 refreshes/second." },
    { term: "Resolution", definition: "The number of pixels on screen (columns × rows), e.g. 1920 × 1080. Higher = sharper image." },
  ]},
  { topic: "Hardware & Software", color: "rose", cards: [
    { term: "Hardware", definition: "The collection of physical components attached to or forming part of the computer system — items you can see and touch." },
    { term: "Software", definition: "The collection of non-physical parts: the operating system, application programs, and data." },
    { term: "Data", definition: "Raw, unorganised facts that need to be processed to become useful and meaningful." },
    { term: "Information", definition: "Data that has been processed, organised, structured or presented in a given context to make it useful." },
    { term: "ICT System", definition: "Information and Communication Technology — a computer system extended to include communication devices for networking." },
    { term: "Synchronisation", definition: "A process where data on a desktop and mobile device is kept consistent, so both devices see the latest version." },
  ]},
  { topic: "Computer Types", color: "violet", cards: [
    { term: "Desktop", definition: "The most common personal computer. Processing power 3–4. Large, not portable. Runs full applications." },
    { term: "Laptop", definition: "Similar to desktop but portable. All peripherals integrated in the casing. Processing power 3–4." },
    { term: "Tablet", definition: "Lightweight mobile computer with a touch screen. Processing power 1–2. Very portable." },
    { term: "Smartphone", definition: "A mobile phone with advanced computing, connectivity, and app capabilities. Processing power 1–2." },
    { term: "Server", definition: "Powerful computer providing services to other computers in a network. Processing power 5–8. Not portable." },
    { term: "Embedded Computer", definition: "A computer system designed to perform dedicated functions within a larger device, e.g. washing machines, cars, ATMs." },
    { term: "Single Board Computer", definition: "E.g. Raspberry Pi, Arduino. Small motherboard with processor and storage. Used for coding, robotics, and smart devices." },
    { term: "Supercomputer", definition: "The most powerful computer, used in computational science, AI, weather forecasting. Processing power 10." },
  ]},
  { topic: "CPU & Memory", color: "amber", cards: [
    { term: "CPU", definition: "Central Processing Unit — the largest chip on the motherboard that executes instructions. Consists of ALU, Control Unit, and Registers." },
    { term: "ALU", definition: "Arithmetic Logic Unit — performs arithmetic (add, subtract, multiply, divide) and logical (>, <, =) calculations." },
    { term: "Control Unit (CU)", definition: "Coordinates instructions to be processed. Controls what the ALU, RAM and I/O devices do." },
    { term: "Registers", definition: "Small, fast memory locations inside the CPU where data/instructions being processed are temporarily stored." },
    { term: "RAM", definition: "Random Access Memory — volatile primary memory. Stores data and instructions currently being processed. Lost when power is off." },
    { term: "ROM", definition: "Read Only Memory — non-volatile memory. Stores permanent instructions like BIOS. Cannot normally be changed." },
    { term: "BIOS", definition: "Basic Input Output System — non-volatile firmware on a ROM chip that initialises hardware when the computer starts up." },
    { term: "Volatile", definition: "Memory that loses its contents when power is switched off. Example: RAM." },
    { term: "Non-volatile", definition: "Memory that retains its contents when power is switched off. Examples: ROM, SSD, HDD." },
    { term: "Von Neumann Architecture", definition: "The design where CPU is directly connected to RAM and programs must be loaded from secondary storage into RAM before execution (1945)." },
  ]},
  { topic: "Storage & Devices", color: "rose", cards: [
    { term: "HDD", definition: "Hard Disk Drive — magnetic, mechanical storage. Higher capacity per cost but slower, with moving parts susceptible to damage." },
    { term: "SSD", definition: "Solid State Drive — electronic storage using microchips. Faster, more durable (no moving parts), but more expensive per GB." },
    { term: "Flash Drive", definition: "Small, portable solid-state storage using a USB port. Used for transferring and backing up data." },
    { term: "SD Card", definition: "Secure Digital — small flash memory card used in cameras, phones, tablets. 16GB+ capacity." },
    { term: "Optical Drive", definition: "Uses laser light to read/write data on CDs, DVDs, and Blu-ray discs. Becoming less common." },
    { term: "Component", definition: "Hardware placed directly on the motherboard, e.g. RAM, CPU." },
    { term: "Peripheral", definition: "Hardware connected to the motherboard but not directly placed on it, e.g. keyboard, mouse, printer." },
  ]},
  { topic: "Input Devices", color: "emerald", cards: [
    { term: "Scanner", definition: "Analyses an image and converts it to a digital file (.jpg or .bmp). OCR software can convert scanned text to editable text." },
    { term: "OCR", definition: "Optical Character Recognition — software that converts scanned text images into editable digital text (UNICODE)." },
    { term: "Barcode Scanner", definition: "Reads black and white barcode patterns at Point of Sale (POS) terminals to identify products and prices." },
    { term: "QR Code", definition: "Quick Response code — a two-dimensional pattern of squares that can store more information than a barcode. Read by smartphone cameras." },
    { term: "Biometric Device", definition: "Technology for identifying people by their physical characteristics: fingerprints, iris/retinal scans, voiceprints." },
    { term: "Sensor", definition: "Converts a real-world property (temperature, speed, light) into a signal a computer can process." },
    { term: "Accelerometer", definition: "A sensor that measures tilting and orientation of a device, e.g. determining phone screen orientation." },
    { term: "Microphone", definition: "Converts sound energy into electrical/digital energy using a vibrating diaphragm." },
  ]},
  { topic: "System Software", color: "teal", cards: [
    { term: "Operating System (OS)", definition: "Software that manages hardware and allows the user to run applications. Functions: user interface, load programs, manage resources." },
    { term: "Kernel", definition: "The smallest, core part of the operating system that is always stored in memory. Loaded when the computer is turned on." },
    { term: "GUI", definition: "Graphical User Interface — uses Windows, Icons, Mouse/Menus, and Pointers (WIMP) for interaction." },
    { term: "CLI", definition: "Command Line Interface — text-based interface where commands are typed at a prompt. No mouse, icons, or menus." },
    { term: "Device Driver", definition: "A small program that allows the operating system to communicate with and send instructions to a specific hardware device." },
    { term: "Defragmentation", definition: "A utility that rearranges fragmented data on an HDD so files are stored contiguously, improving performance. Not needed for SSDs." },
    { term: "Source Code", definition: "The original code written by a programmer (.pas for Delphi, .java for Java)." },
    { term: "Executable Code", definition: "Compiled object code that the computer can run (.exe for Delphi, .class for Java)." },
    { term: "IDE", definition: "Integrated Development Environment — a tool with source code editor, compiler/interpreter, and debugging tools." },
  ]},
  { topic: "Software Licensing", color: "indigo", cards: [
    { term: "Proprietary License", definition: "Software that cannot be freely distributed or copied. Requires purchase/activation keys. No access to source code. E.g. Microsoft Windows." },
    { term: "Open Source License", definition: "Source code is shared. Users can modify and redistribute. Uses copyleft licenses like GPL. E.g. Linux." },
    { term: "Creative Commons", definition: "Allows copying and distributing work on condition the creator is acknowledged." },
    { term: "Freeware", definition: "Software given away free by the author but still copyrighted. Cannot be altered or resold. Source code not provided." },
    { term: "Freemium", definition: "Limited functions free; advanced features require payment for a licence key." },
    { term: "Copyright", definition: "Legal protection establishing that authors/creators have ownership of their software." },
    { term: "Piracy", definition: "The illegal copying of software, violating the software licence." },
  ]},
];

const colorMap = {
  sky:    { border: "border-sky-200",    accent: "bg-sky-500",    text: "text-sky-700",    badge: "bg-sky-100 text-sky-700" },
  violet: { border: "border-violet-200", accent: "bg-violet-500", text: "text-violet-700", badge: "bg-violet-100 text-violet-700" },
  amber:  { border: "border-amber-200",  accent: "bg-amber-500",  text: "text-amber-700",  badge: "bg-amber-100 text-amber-700" },
  rose:   { border: "border-rose-200",   accent: "bg-rose-500",   text: "text-rose-700",   badge: "bg-rose-100 text-rose-700" },
  emerald:{ border: "border-emerald-200",accent: "bg-emerald-500",text: "text-emerald-700",badge: "bg-emerald-100 text-emerald-700" },
  teal:   { border: "border-teal-200",   accent: "bg-teal-500",   text: "text-teal-700",   badge: "bg-teal-100 text-teal-700" },
  indigo: { border: "border-indigo-200", accent: "bg-indigo-500", text: "text-indigo-700", badge: "bg-indigo-100 text-indigo-700" },
};

const ALL_CARDS: FlashcardWithTopic[] = FLASHCARDS.flatMap(g =>
  g.cards.map(c => ({ ...c, topic: g.topic, color: g.color }))
);

// ─── Sub-components ───────────────────────────────────────────────────────────

function Spoiler({ label = "Show Answer", children }: { label?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="my-3">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm font-semibold tracking-wide uppercase px-3 py-1.5 rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors">
        {open ? <EyeOff size={14} /> : <Eye size={14} />}
        {open ? "Hide Answer" : label}
      </button>
      {open && <div className="mt-2 p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm leading-relaxed font-mono whitespace-pre-wrap">{children}</div>}
    </div>
  );
}

function Section({ id, title, completed, onToggle, defaultOpen = false, children }: {
  id: string; title: string; completed: boolean; onToggle: () => void; defaultOpen?: boolean; children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section id={id} className="mb-6 scroll-mt-24">
      <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setOpen(!open)}>
        <button onClick={(e) => { e.stopPropagation(); onToggle(); }} className="shrink-0 text-slate-400 hover:text-emerald-600 transition-colors" title="Mark as completed">
          {completed ? <CheckSquare size={20} className="text-emerald-600" /> : <Square size={20} />}
        </button>
        <h2 className={`flex-1 text-lg md:text-xl font-bold tracking-tight ${completed ? "text-emerald-700" : "text-slate-800"}`}>{title}</h2>
        <span className="text-slate-400 group-hover:text-slate-600 transition-colors">{open ? <ChevronDown size={20} /> : <ChevronRight size={20} />}</span>
      </div>
      <div className={`border-l-2 ml-2.5 pl-6 mt-2 ${open ? "border-amber-300" : "border-transparent"}`}>
        {open && <div className="pb-2 text-slate-700 text-sm md:text-base leading-relaxed">{children}</div>}
      </div>
    </section>
  );
}

function InfoBox({ title, children, color = "blue" }: { title?: string; children: ReactNode; color?: "blue" | "green" | "orange" | "red" }) {
  const c = {
    blue:   "bg-sky-50 border-sky-300 text-sky-900",
    green:  "bg-emerald-50 border-emerald-300 text-emerald-900",
    orange: "bg-orange-50 border-orange-300 text-orange-900",
    red:    "bg-red-50 border-red-300 text-red-900",
  };
  return (
    <div className={`my-4 p-4 rounded-lg border ${c[color]}`}>
      {title && <div className="font-bold mb-1">{title}</div>}
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: ReactNode[][] }) {
  return (
    <div className="overflow-x-auto my-4">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>{headers.map((h, i) => <th key={i} className="text-left px-3 py-2 bg-slate-100 border border-slate-200 font-semibold text-slate-700">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-slate-50"}>
              {row.map((cell, ci) => <td key={ci} className="px-3 py-2 border border-slate-200">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Flashcard({ term, definition, topic, color, flipped, onFlip }: {
  term: string; definition: string; topic: string; color: keyof typeof colorMap; flipped: boolean; onFlip: () => void;
}) {
  const c = colorMap[color] ?? colorMap.sky;
  return (
    <div onClick={onFlip} className={`relative cursor-pointer rounded-xl border-2 ${c.border} overflow-hidden transition-all duration-200 hover:shadow-lg active:scale-[0.98] select-none`} style={{ minHeight: "180px" }}>
      <div className={`absolute top-0 left-0 right-0 h-1.5 ${c.accent}`} />
      <div className="p-5 flex flex-col h-full justify-between" style={{ minHeight: "180px" }}>
        <div>
          <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${c.badge} mb-3`}>{topic}</span>
          {!flipped
            ? <div><p className="text-lg font-bold text-slate-800 leading-snug">{term}</p><p className="text-xs text-slate-400 mt-3 italic">Tap to reveal definition</p></div>
            : <div><p className={`text-xs font-semibold uppercase tracking-wider ${c.text} mb-1`}>{term}</p><p className="text-sm text-slate-700 leading-relaxed">{definition}</p></div>
          }
        </div>
      </div>
    </div>
  );
}

function CheatSheet() {
  const [mode, setMode] = useState<"browse" | "quiz">("browse");
  const [filter, setFilter] = useState("all");
  const [flippedCards, setFlippedCards] = useState<Record<string, boolean>>({});
  const [quizOrder, setQuizOrder] = useState<FlashcardWithTopic[]>([]);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizFlipped, setQuizFlipped] = useState(false);
  const [knownCount, setKnownCount] = useState(0);

  const filteredCards = filter === "all" ? ALL_CARDS : ALL_CARDS.filter(c => c.topic === filter);

  const startQuiz = (shuffle = true) => {
    const cards = [...filteredCards];
    if (shuffle) {
      for (let i = cards.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [cards[i], cards[j]] = [cards[j], cards[i]];
      }
    }
    setQuizOrder(cards); setQuizIndex(0); setQuizFlipped(false); setKnownCount(0); setMode("quiz");
  };

  const topics = ["all", ...FLASHCARDS.map(g => g.topic)];

  if (mode === "quiz") {
    if (quizOrder.length === 0) return null;
    const done = quizIndex >= quizOrder.length;
    const card = !done ? quizOrder[quizIndex] : null;
    return (
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setMode("browse")} className="text-sm text-slate-500 hover:text-slate-700">← Back to browse</button>
          <span className="text-sm font-semibold text-slate-600">{done ? "Complete!" : `${quizIndex + 1} / ${quizOrder.length}`}</span>
        </div>
        <div className="w-full h-2 rounded-full bg-slate-200 mb-6 overflow-hidden">
          <div className="h-full rounded-full bg-emerald-500 transition-all duration-300" style={{ width: `${(quizIndex / quizOrder.length) * 100}%` }} />
        </div>
        {done ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">🎉</div>
            <p className="text-xl font-bold text-slate-800 mb-2">All cards reviewed!</p>
            <p className="text-slate-600 mb-1">You marked <strong className="text-emerald-600">{knownCount}</strong> out of <strong>{quizOrder.length}</strong> as known.</p>
            <p className="text-sm text-slate-500 mb-6">
              {knownCount === quizOrder.length ? "Perfect!" : knownCount >= quizOrder.length * 0.7 ? "Great work! Review the ones you missed." : "Keep studying!"}
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button onClick={() => startQuiz(true)} className="px-4 py-2 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors flex items-center gap-2"><Shuffle size={16} /> Retry</button>
              <button onClick={() => setMode("browse")} className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 font-semibold hover:bg-slate-300 transition-colors">Browse</button>
            </div>
          </div>
        ) : (
          <div>
            <Flashcard term={card!.term} definition={card!.definition} topic={card!.topic} color={card!.color} flipped={quizFlipped} onFlip={() => setQuizFlipped(!quizFlipped)} />
            {quizFlipped && (
              <div className="flex gap-3 justify-center mt-5">
                <button onClick={() => { setQuizIndex(i => i + 1); setQuizFlipped(false); }} className="px-5 py-2.5 rounded-lg bg-rose-100 text-rose-700 font-semibold hover:bg-rose-200 transition-colors">Still learning</button>
                <button onClick={() => { setKnownCount(k => k + 1); setQuizIndex(i => i + 1); setQuizFlipped(false); }} className="px-5 py-2.5 rounded-lg bg-emerald-100 text-emerald-700 font-semibold hover:bg-emerald-200 transition-colors">✓ I know this</button>
              </div>
            )}
            {!quizFlipped && <p className="text-center text-sm text-slate-400 mt-4 italic">Tap the card to reveal the definition</p>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap gap-1.5">
          {topics.map(tp => (
            <button key={tp} onClick={() => { setFilter(tp); setFlippedCards({}); }}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${filter === tp ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {tp === "all" ? `All (${ALL_CARDS.length})` : tp}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setFlippedCards({})} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 flex items-center gap-1 transition-colors"><RotateCcw size={12} /> Reset</button>
          <button onClick={() => startQuiz(true)} className="text-xs px-3 py-1.5 rounded-full bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-1.5 font-semibold transition-colors"><Shuffle size={12} /> Quiz me</button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">Showing {filteredCards.length} term{filteredCards.length !== 1 ? "s" : ""} · Tap a card to reveal its definition</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {filteredCards.map((card, idx) => {
          const gid = `${filter}-${idx}`;
          return <Flashcard key={gid} term={card.term} definition={card.definition} topic={card.topic} color={card.color} flipped={!!flippedCards[gid]} onFlip={() => setFlippedCards(prev => ({ ...prev, [gid]: !prev[gid] }))} />;
        })}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function StudyGuideGr10({ onBack }: { onBack?: () => void }) {
  const [completed, setCompleted] = useState<Record<string, boolean>>({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [activeTab, setActiveTab] = useState<"study" | "cheatsheet">("study");
  const [expandedLUs, setExpandedLUs] = useState<Record<number, boolean>>(() => ({ 1: true }));
  const toggleLU = (lu: number) => setExpandedLUs((p) => ({ ...p, [lu]: !p[lu] }));

  useEffect(() => {
    const h = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const toggle = (id: string) => setCompleted(c => ({ ...c, [id]: !c[id] }));
  const progress = SECTIONS.filter(s => completed[s.id]).length;
  const scrollTo = (id: string) => {
    setActiveTab("study");
    setTimeout(() => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); }, 50);
    setMenuOpen(false);
  };

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #faf5ef 0%, #f0ebe3 50%, #e8e0d4 100%)", fontFamily: "'Georgia', 'Cambria', serif" }}>
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-amber-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="p-1.5 rounded hover:bg-amber-100 text-amber-700 text-sm flex items-center gap-1 shrink-0">
              ← Back
            </button>
          )}
          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-slate-600">{menuOpen ? <X size={22} /> : <Menu size={22} />}</button>
          <BookOpen size={22} className="text-amber-700 shrink-0 hidden md:block" />
          <div className="flex-1 min-w-0">
            <h1 className="text-base md:text-lg font-bold text-slate-800 truncate" style={{ fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" }}>
              Data Representation · Hardware · System Software
            </h1>
            <p className="text-xs text-slate-500">Grade 10 · Learning Units 1, 2, 3 &amp; 4 · Interactive Study Guide</p>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <div className="w-24 h-2 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${(progress / SECTIONS.length) * 100}%` }} />
            </div>
            <span className="text-xs font-semibold text-slate-500">{progress}/{SECTIONS.length}</span>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 flex gap-0">
          <button onClick={() => { setActiveTab("study"); setMenuOpen(false); }} className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === "study" ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <span className="flex items-center gap-1.5"><BookOpen size={14} /> Study Guide</span>
          </button>
          <button onClick={() => { setActiveTab("cheatsheet"); setMenuOpen(false); }} className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === "cheatsheet" ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <span className="flex items-center gap-1.5"><Layers size={14} /> Flashcards</span>
          </button>
        </div>
        {/* ─── Mobile drawer nav (LU-grouped). Desktop uses the sidebar below. ─── */}
        {activeTab === "study" && (
          <nav className={`${menuOpen ? "block" : "hidden"} md:hidden border-t border-amber-100 bg-white/60 max-h-[70vh] overflow-y-auto`}>
            <div className="max-w-4xl mx-auto px-4 py-2 space-y-1">
              {LU_NUMBERS.map((lu) => {
                const sections = SECTIONS_BY_LU[lu] || [];
                const isExpanded = !!expandedLUs[lu];
                const luProgress = sections.filter((s) => completed[s.id]).length;
                return (
                  <div key={lu}>
                    <button
                      onClick={() => toggleLU(lu)}
                      className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-semibold text-slate-700 hover:bg-amber-50"
                    >
                      {isExpanded ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0" />}
                      <span className="flex-1 text-left">{lu === 0 ? LU_TITLES[0] : `LU${lu} — ${LU_TITLES[lu]}`}</span>
                      <span className={`text-[10px] tabular-nums ${luProgress === sections.length ? "text-emerald-600 font-bold" : "text-slate-400"}`}>{luProgress}/{sections.length}</span>
                    </button>
                    {isExpanded && (
                      <div className="ml-3 mt-0.5 border-l border-amber-100 flex flex-col">
                        {sections.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => scrollTo(s.id)}
                            className={`text-left text-xs pl-3 pr-2 py-1 transition-colors ${
                              completed[s.id]
                                ? "text-emerald-700 font-semibold"
                                : "text-slate-500 hover:text-amber-800"
                            }`}
                          >
                            {s.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>
        )}
      </header>

      {/* ─── Content (with desktop sidebar at md+) ─── */}
      <div className="md:flex md:items-start md:max-w-7xl md:mx-auto md:px-4 md:gap-6">
      {activeTab === "study" && (
        <aside className="hidden md:block md:w-60 md:shrink-0 md:sticky md:top-28 md:self-start md:max-h-[calc(100vh-7rem)] md:overflow-y-auto md:py-8">
          <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3 px-2">Contents</h3>
          <nav className="space-y-0.5">
            {LU_NUMBERS.map((lu) => {
              const sections = SECTIONS_BY_LU[lu] || [];
              const isExpanded = !!expandedLUs[lu];
              const luProgress = sections.filter((s) => completed[s.id]).length;
              return (
                <div key={lu}>
                  <button
                    onClick={() => toggleLU(lu)}
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-semibold text-slate-700 hover:bg-amber-50 transition-colors"
                  >
                    {isExpanded ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0" />}
                    <span className="flex-1 text-left truncate">{lu === 0 ? LU_TITLES[0] : `LU${lu} — ${LU_TITLES[lu]}`}</span>
                    <span className={`text-[10px] tabular-nums ${luProgress === sections.length && sections.length > 0 ? "text-emerald-600 font-bold" : "text-slate-400"}`}>{luProgress}/{sections.length}</span>
                  </button>
                  {isExpanded && (
                    <div className="ml-3 mt-0.5 border-l border-amber-100 flex flex-col">
                      {sections.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => scrollTo(s.id)}
                          className={`text-left text-xs pl-3 pr-2 py-1 transition-colors ${
                            completed[s.id]
                              ? "text-emerald-700 font-semibold"
                              : "text-slate-500 hover:text-amber-800"
                          }`}
                        >
                          {s.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>
      )}
      <main className="max-w-4xl mx-auto px-4 py-8 md:flex-1 md:max-w-none md:mx-0 md:px-0">

        {activeTab === "cheatsheet" && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-800 mb-1" style={{ fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" }}>Term Flashcards</h2>
              <p className="text-sm text-slate-600">{ALL_CARDS.length} terms across {FLASHCARDS.length} topics. Tap a card to reveal its definition, or hit <strong>Quiz me</strong> to test yourself.</p>
            </div>
            <CheatSheet />
          </div>
        )}

        {activeTab === "study" && (
          <div>

          {/* ===== LU1: DATA REPRESENTATION AND STORAGE ===== */}

          <Section id="number-systems" title="1.2 Number Systems (Decimal, Binary, Hexadecimal)" completed={!!completed["number-systems"]} onToggle={() => toggle("number-systems")} defaultOpen={true}>
            <p>Computers are <strong>two-state machines</strong> — they can only understand 0s and 1s (binary). We use three number systems:</p>
            <Table headers={["System", "Base", "Digits Used", "Column Values"]} rows={[
              ["Decimal", "10", "0–9", "1, 10, 100, 1000…"],
              ["Binary", "2", "0, 1", "1, 2, 4, 8, 16, 32, 64, 128…"],
              ["Hexadecimal", "16", "0–9, A–F", "1, 16, 256, 4096…"],
            ]} />
            <InfoBox title="Powers of 2 — Memorise These!" color="green">
              2⁰=1, 2¹=2, 2²=4, 2³=8, 2⁴=16, 2⁵=32, 2⁶=64, 2⁷=128, 2⁸=256
            </InfoBox>
            <InfoBox title="Hex Digits" color="blue">
              A=10, B=11, C=12, D=13, E=14, F=15. Each hex digit = exactly 4 binary bits.
            </InfoBox>
          </Section>

          <Section id="conversions" title="1.2 Conversions Between Bases" completed={!!completed["conversions"]} onToggle={() => toggle("conversions")}>
            <h3 className="font-bold text-slate-800 mt-2 mb-2 text-base">Binary → Decimal</h3>
            <p>Write column values (powers of 2) above each bit, multiply, and add:</p>
            <InfoBox color="green">
              <div className="font-mono text-center">1101010₂ = 64+32+0+8+0+2+0 = 106₁₀</div>
            </InfoBox>
            <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Decimal → Binary</h3>
            <p>Divide by 2 repeatedly, write remainders, read from <strong>bottom to top</strong>:</p>
            <InfoBox color="green">
              <div className="font-mono text-sm">106÷2=53 r<strong>0</strong> → 53÷2=26 r<strong>1</strong> → 26÷2=13 r<strong>0</strong> → 13÷2=6 r<strong>1</strong> → 6÷2=3 r<strong>0</strong> → 3÷2=1 r<strong>1</strong> → 1÷2=0 r<strong>1</strong></div>
              <div className="font-mono text-center mt-1">Read bottom→top: <strong>1101010₂</strong></div>
            </InfoBox>
            <p className="mt-2"><strong>Alternative method:</strong> Subtract the largest power of 2 that fits, place a 1 in that column, repeat.</p>
            <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Decimal → Hexadecimal</h3>
            <p>Divide by 16. Remainders ≥ 10 become letters (10=A, 11=B… 15=F). Read bottom to top.</p>
            <InfoBox color="green">
              <div className="font-mono text-sm">223÷16=13 r<strong>15(F)</strong> → 13÷16=0 r<strong>13(D)</strong></div>
              <div className="font-mono text-center mt-1">Read bottom→top: <strong>DF₁₆</strong></div>
            </InfoBox>
            <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Hexadecimal → Decimal</h3>
            <p>Multiply each digit by its power of 16 and add:</p>
            <InfoBox color="green">
              <div className="font-mono text-center">ACE₁₆ = 10×16² + 12×16¹ + 14×16⁰ = 2560+192+14 = 2766₁₀</div>
            </InfoBox>
            <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Binary ↔ Hexadecimal (Direct)</h3>
            <p>Split binary into groups of <strong>4 bits</strong> (pad with leading 0s if needed). Convert each group to one hex digit.</p>
            <InfoBox color="green">
              <div className="font-mono text-center">1111011₂ → 0111 1011 → 7B₁₆</div>
              <div className="font-mono text-center mt-1">DF₁₆ → D=1101 F=1111 → 11011111₂</div>
            </InfoBox>
            <div className="mt-4 font-semibold text-slate-700">Practice:</div>
            <p className="mt-1">Convert 10101111₂ to decimal:</p>
            <Spoiler>{`128+0+32+0+8+4+2+1 = 175`}</Spoiler>
            <p className="mt-2">Convert 65₁₀ to binary:</p>
            <Spoiler>{`65÷2=32r1, 32÷2=16r0, 16÷2=8r0, 8÷2=4r0, 4÷2=2r0, 2÷2=1r0, 1÷2=0r1\nRead bottom→top: 1000001₂`}</Spoiler>
            <p className="mt-2">Convert 82₁₀ to hexadecimal:</p>
            <Spoiler>{`82÷16=5 r2 → 5÷16=0 r5\nRead bottom→top: 52₁₆`}</Spoiler>
          </Section>

          <Section id="word-size" title="1.3 Word Size & Combinations" completed={!!completed["word-size"]} onToggle={() => toggle("word-size")}>
            <p>The <strong>word size</strong> is the number of bits a CPU can process at one time. Bits are grouped into <strong>words</strong>. Current CPUs use 32 or 64 bits.</p>
            <InfoBox title="Key Formula" color="green">
              <div className="text-center text-lg font-bold">Number of combinations = 2ⁿ</div>
              <div className="text-center mt-1">where n = number of bits</div>
            </InfoBox>
            <Table headers={["Bits (n)", "Combinations (2ⁿ)"]} rows={[
              ["4", "16"], ["7", "128"], ["8", "256"], ["10", "1,024"], ["16", "65,536"],
            ]} />
            <div className="mt-4 font-semibold text-slate-700">Practice:</div>
            <p className="mt-1">How many unique values can 12 bits represent?</p>
            <Spoiler>2¹² = 4,096</Spoiler>
          </Section>

          <Section id="data-storage" title="1.4 Data Storage Units" completed={!!completed["data-storage"]} onToggle={() => toggle("data-storage")}>
            <p>Storage is measured in multiples of <strong>bytes</strong> (not bits). Note: 1 KB = <strong>1024</strong> bytes, not 1000.</p>
            <Table headers={["Unit", "Abbreviation", "Size"]} rows={[
              ["Byte", "B", "8 bits"],
              ["Kilobyte", "KB", "1,024 bytes = 2¹⁰"],
              ["Megabyte", "MB", "1,024 KB = 2²⁰"],
              ["Gigabyte", "GB", "1,024 MB = 2³⁰"],
              ["Terabyte", "TB", "1,024 GB = 2⁴⁰"],
            ]} />
            <InfoBox title="bits vs Bytes" color="orange">A lowercase <strong>b</strong> = bits. An uppercase <strong>B</strong> = Bytes. 8 bits = 1 Byte.</InfoBox>
          </Section>

          <Section id="integers" title="1.5 Representing Integers" completed={!!completed["integers"]} onToggle={() => toggle("integers")}>
            <p>Integers are whole numbers (no decimal part). They are stored in a fixed number of bits, which limits their range.</p>
            <h3 className="font-bold text-slate-800 mt-4 mb-2 text-base">Unsigned Integers</h3>
            <p>All bits represent the value. No negative numbers.</p>
            <InfoBox title="Unsigned Range" color="green">
              <div>Smallest: <strong>0</strong></div>
              <div>Largest: <strong>2ⁿ − 1</strong></div>
              <div className="mt-1">E.g. 8 bits: 0 to 255. 4 bits: 0 to 15.</div>
            </InfoBox>
            <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Signed Integers</h3>
            <p>The leftmost bit indicates the sign: <strong>0 = positive</strong>, <strong>1 = negative</strong>. This reduces the range because one bit is "used up" for the sign.</p>
            <InfoBox title="Signed Range" color="green">
              <div>Smallest: <strong>−2ⁿ⁻¹</strong></div>
              <div>Largest: <strong>2ⁿ⁻¹ − 1</strong></div>
              <div className="mt-1">E.g. 8 bits: −128 to 127. 4 bits: −8 to 7.</div>
            </InfoBox>
            <Table headers={["Bits", "Unsigned Range", "Signed Range"]} rows={[
              ["4", "0 to 15", "−8 to 7"],
              ["6", "0 to 63", "−32 to 31"],
              ["8", "0 to 255", "−128 to 127"],
            ]} />
            <div className="mt-4 font-semibold text-slate-700">Practice:</div>
            <p className="mt-1">What is the range of a 16-bit unsigned integer?</p>
            <Spoiler>0 to 2¹⁶ − 1 = 0 to 65,535</Spoiler>
            <p className="mt-2">What is the range of a 16-bit signed integer?</p>
            <Spoiler>−2¹⁵ to 2¹⁵ − 1 = −32,768 to 32,767</Spoiler>
          </Section>

          <Section id="characters" title="1.6 Character Representation (ASCII, Unicode, UTF-8)" completed={!!completed["characters"]} onToggle={() => toggle("characters")}>
            <p>Computers store characters as numbers. Different encoding systems map numbers to characters.</p>
            <Table headers={["Encoding", "Bits", "Characters", "Limitation"]} rows={[
              ["ASCII", "8 bits", "256", "Limited — couldn't represent all languages"],
              ["Unicode", "Variable", "1,112,064+", "Universal — every character in every language"],
              ["UTF-8", "8–32 bits", "Same as Unicode", "Variable-length: 1–4 bytes per character"],
            ]} />
            <InfoBox title="Why Unicode replaced ASCII" color="blue">
              ASCII couldn't handle languages like Afrikaans (ë, ê), Chinese, Arabic, etc. Different computers used different extended encodings, causing data corruption when sharing files. Unicode gives every character a unique, universal number.
            </InfoBox>
            <p className="mt-2">In UTF-8, each group of 8 bits (1 byte) is called an <strong>octet</strong>. A character set of 4 bytes can represent 2³² ≈ 1.1 million characters.</p>
          </Section>

          <Section id="screen-data" title="1.7 Screen Data, RGB & Resolution" completed={!!completed["screen-data"]} onToggle={() => toggle("screen-data")}>
            <p>Screens display images as a grid of <strong>pixels</strong>. Each pixel's colour is stored using <strong>RGB</strong> (Red, Green, Blue).</p>
            <InfoBox title="RGB Colour Model" color="green">
              <div>Each colour channel (R, G, B) = 1 byte (0–255)</div>
              <div><strong>3 bytes per pixel</strong> = 2²⁴ = 16,777,216 possible colours</div>
              <div className="mt-2 font-mono text-sm">Black = (0, 0, 0) · White = (255, 255, 255) · Red = (255, 0, 0)</div>
            </InfoBox>
            <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Screen Memory Calculation</h3>
            <InfoBox title="Formula" color="blue">
              <div className="font-mono text-center">Memory per second = width × height × 3 bytes × refresh rate (Hz)</div>
            </InfoBox>
            <p className="mt-2"><strong>Resolution</strong> = number of pixels (e.g. 1920 × 1080). <strong>Refresh rate</strong> = redraws per second in Hz (e.g. 60Hz, 144Hz).</p>
            <div className="mt-4 font-semibold text-slate-700">Practice:</div>
            <p className="mt-1">Calculate the memory needed per second for a 1920×1080 screen at 120Hz:</p>
            <Spoiler>{`1920 × 1080 × 3 bytes × 120\n= 1920 × 1080 = 2,073,600 pixels\n× 3 = 6,220,800 bytes per frame\n× 120 = 746,496,000 bytes/sec\n≈ 746.5 MB per second`}</Spoiler>
          </Section>

          {/* ===== LU2: OVERVIEW OF A COMPUTER ===== */}

          <Section id="hw-sw" title="2.1 Hardware and Software" completed={!!completed["hw-sw"]} onToggle={() => toggle("hw-sw")}>
            <p><strong>Hardware</strong> is the collection of physical components you can see and touch. <strong>Software</strong> is the non-physical part: operating system, applications, and data.</p>
            <InfoBox title="Key Relationship" color="green">Neither can exist on its own — hardware needs software to tell it what to do, and software needs hardware to perform its tasks.</InfoBox>
            <p className="mt-2">Together, hardware and software interact to: receive <strong>input</strong>, <strong>process</strong> data, <strong>store</strong> data, and send <strong>output</strong>.</p>
            <div className="mt-3 font-semibold text-slate-700">Quick check:</div>
            <p className="mt-1">A printer is hardware. Without software, can it print a document?</p>
            <Spoiler>No. A printer needs software (a print driver and the application) to tell it what to print and how. Hardware without software will stand idle.</Spoiler>
          </Section>

          <Section id="ipc" title="2.2 General Processing Cycle & ICT" completed={!!completed["ipc"]} onToggle={() => toggle("ipc")}>
            <p>The general processing cycle shows how data flows through a computer:</p>
            <InfoBox title="Processing Cycle" color="blue">
              <div className="text-center font-bold">INPUT → RAM → CPU → OUTPUT</div>
              <div className="text-center mt-1">↕ Secondary Storage</div>
            </InfoBox>
            <p className="mt-2">All data must be stored in <strong>RAM</strong> before the CPU can process it. The CPU cannot execute instructions directly from secondary storage.</p>
            <p className="mt-3">With communication devices added, the model becomes an <strong>ICT system</strong> (Information and Communication Technology), allowing computers to exchange data over networks.</p>
            <div className="mt-3 font-semibold text-slate-700">Quick check:</div>
            <p className="mt-1">Can the CPU process data directly from the hard drive?</p>
            <Spoiler>No. Data must first be loaded from secondary storage into RAM. The CPU is directly connected to RAM, not the hard drive.</Spoiler>
          </Section>

          <Section id="data-info" title="2.3 Data and Information" completed={!!completed["data-info"]} onToggle={() => toggle("data-info")}>
            <p><strong>Data</strong> = raw, unorganised facts. <strong>Information</strong> = processed data with context and meaning.</p>
            <InfoBox title="Example" color="green">
              A spreadsheet with names and ages (data) → processed to calculate the highest age, lowest age, and average (information).
            </InfoBox>
            <p className="mt-2">The numbers in a spreadsheet are <strong>input</strong> as <strong>data</strong>, <strong>processed</strong>, and the result is <strong>output</strong> as <strong>information</strong>.</p>
            <div className="mt-3 font-semibold text-slate-700">Quick check:</div>
            <p className="mt-1">A vet clinic records a pet's weight as 12.5 kg. Is this data or information?</p>
            <Spoiler>It is data — a raw, unprocessed fact. It becomes information when processed, e.g. "The pet is overweight compared to breed average" or shown on a weight trend graph.</Spoiler>
          </Section>

          <Section id="comp-types" title="2.4 Computer System Types" completed={!!completed["comp-types"]} onToggle={() => toggle("comp-types")}>
            <p>Computers are categorised by processing power, size, and portability:</p>
            <Table headers={["Type", "Processing Power", "Portability"]} rows={[
              ["Desktop", "3–4", "Not portable"],
              ["Laptop", "3–4", "Very portable"],
              ["Tablet", "1–2", "Very portable"],
              ["Smartphone", "1–2", "Very portable"],
              ["Smart wear", "1", "Very portable"],
              ["Server", "5–8", "Not portable"],
              ["Embedded", "N/A", "N/A"],
              ["Single Board (RPi)", "1", "Very portable"],
              ["Supercomputer", "10", "Not at all"],
            ]} />
            <InfoBox title="Stand-alone vs Network Application" color="blue">
              <strong>Stand-alone:</strong> Runs on one computer without needing a network (e.g. MS Word offline).<br />
              <strong>Network:</strong> Runs on multiple computers connected via a network, accessing a server (e.g. Google Docs, online banking).
            </InfoBox>
            <div className="mt-3 font-semibold text-slate-700">Quick check:</div>
            <p className="mt-1">What type of computer is found in a traffic light?</p>
            <Spoiler>Embedded computer — it performs a dedicated function within a larger device.</Spoiler>
          </Section>

          <Section id="os" title="2.5 Operating Systems" completed={!!completed["os"]} onToggle={() => toggle("os")}>
            <p>Every computer needs an <strong>operating system</strong>. The <strong>kernel</strong> is always stored in memory and loads when the computer turns on.</p>
            <InfoBox title="Three Functions of an OS" color="green">
              <div>1. Provide a <strong>user interface</strong> (GUI or CLI)</div>
              <div>2. <strong>Load and run</strong> programs</div>
              <div>3. <strong>Manage resources</strong> (CPU, RAM, storage, I/O)</div>
            </InfoBox>
            <Table headers={["OS Type", "Examples", "Used On"]} rows={[
              ["Desktop OS", "Windows, macOS, Linux (Ubuntu)", "Desktops, laptops"],
              ["Mobile OS", "Android, iOS", "Smartphones, tablets"],
              ["Embedded OS", "WatchOS, custom firmware", "Smart watches, ATMs, cars"],
            ]} />
            <div className="mt-3 font-semibold text-slate-700">Quick check:</div>
            <p className="mt-1">Is Android a desktop or mobile operating system?</p>
            <Spoiler>Mobile operating system — designed for devices with limited resources like smartphones and tablets.</Spoiler>
          </Section>

          <Section id="app-sw" title="2.6 Application Software" completed={!!completed["app-sw"]} onToggle={() => toggle("app-sw")}>
            <p>Application software carries out tasks for users. Test: "Will the computer run without this software?" If YES → it's application software.</p>
            <InfoBox title="Stand-alone vs Network" color="blue">
              <strong>Stand-alone:</strong> Runs on one computer (e.g. Word, Photoshop).<br />
              <strong>Network:</strong> Multiple users access shared data over a network via a server (e.g. email, banking apps).
            </InfoBox>
            <p className="mt-2">In a <strong>client-server network</strong>, the server is the <strong>remote</strong> computer providing services, and the client is the <strong>local</strong> computer accessing them.</p>
          </Section>

          {/* ===== LU3: HARDWARE ===== */}

          <Section id="cpu" title="3.1 The CPU" completed={!!completed["cpu"]} onToggle={() => toggle("cpu")}>
            <p>The <strong>CPU</strong> is the largest chip on the motherboard. It continually interprets and executes instructions stored in RAM.</p>
            <InfoBox title="Three Sub-components" color="green">
              <div><strong>ALU</strong> — performs all arithmetic and logical calculations</div>
              <div><strong>Control Unit (CU)</strong> — coordinates instructions and controls what ALU, RAM and I/O do</div>
              <div><strong>Registers</strong> — small, fast memory holding data currently being processed</div>
            </InfoBox>
            <p className="mt-2">The CPU design (connecting CPU to RAM and secondary storage) was first developed in 1945 and is called <strong>von Neumann architecture</strong>.</p>
            <InfoBox title="Don't Confuse!" color="orange">The <strong>System Unit</strong> (the big box/tower) is NOT the CPU. The CPU is a single chip on the motherboard inside the system unit.</InfoBox>
            <div className="mt-3 font-semibold text-slate-700">Quick check:</div>
            <p className="mt-1">Which sub-component of the CPU decides whether 5 &gt; 3 is true or false?</p>
            <Spoiler>The ALU (Arithmetic Logic Unit) — it performs logical comparisons using relational operators like &gt;, &lt;, =.</Spoiler>
          </Section>

          <Section id="memory" title="3.2 Primary Memory (BIOS, RAM, ROM)" completed={!!completed["memory"]} onToggle={() => toggle("memory")}>
            <p>Primary memory is on or directly connected to the motherboard.</p>
            <Table headers={["Type", "Volatile?", "Purpose"]} rows={[
              ["BIOS (on ROM)", "No", "Initialises hardware at startup"],
              ["RAM (DRAM)", "Yes", "Stores data/instructions being processed"],
              ["ROM", "No", "Stores permanent firmware (e.g. BIOS)"],
            ]} />
            <InfoBox title="Volatile vs Non-volatile" color="green">
              <strong>Volatile:</strong> Loses contents when power is off (RAM).<br />
              <strong>Non-volatile:</strong> Retains contents when power is off (ROM, SSD, HDD).
            </InfoBox>
            <p className="mt-2"><strong>Firmware</strong> is software stored in flash ROM of a hardware device. It's "semi-permanent" — it stays the same unless updated by a firmware updater.</p>
            <p className="mt-2"><strong>EEPROM</strong> (Electronically Erasable Programmable ROM) can be changed using software, e.g. for reprogramming a robotics controller.</p>
            <div className="mt-3 font-semibold text-slate-700">Quick check:</div>
            <p className="mt-1">Why is the CPU directly connected to RAM and not the hard drive?</p>
            <Spoiler>RAM is much faster than a hard drive. The CPU needs to access instructions quickly. Programs must be loaded from secondary storage into RAM before the CPU can execute them.</Spoiler>
          </Section>

          <Section id="storage" title="3.3 Secondary Storage" completed={!!completed["storage"]} onToggle={() => toggle("storage")}>
            <p>Secondary storage is <strong>non-volatile</strong> and permanent. It can be fixed or removable.</p>
            <Table headers={["Type", "Technology", "Speed", "Durability", "Cost/GB"]} rows={[
              ["HDD", "Magnetic, mechanical", "Slower", "Moving parts — fragile", "Cheaper"],
              ["SSD", "Solid-state, electronic", "Much faster", "No moving parts — durable", "More expensive"],
              ["Hybrid", "HDD + SSD flash", "Medium", "Mixed", "Medium"],
              ["Flash Drive", "Solid-state, USB", "Fast", "Durable, portable", "Medium"],
              ["SD Card", "Solid-state, flash", "Fast", "Very small", "Medium"],
              ["Optical (CD/DVD)", "Laser", "Slow", "Scratches easily", "Cheap"],
            ]} />
            <InfoBox title="Key Comparison: HDD vs SSD" color="blue">
              <strong>HDD advantage:</strong> More storage for less money.<br />
              <strong>SSD advantage:</strong> Much faster, no moving parts, less power, silent.<br />
              SSDs do <strong>not</strong> need defragmentation (no mechanical read head).
            </InfoBox>
            <div className="mt-3 font-semibold text-slate-700">Quick check:</div>
            <p className="mt-1">Why should you not wave a strong magnet near an HDD?</p>
            <Spoiler>HDDs store data magnetically. A strong magnet can disrupt the magnetic patterns on the disk, corrupting or erasing data.</Spoiler>
          </Section>

          <Section id="input" title="3.4 Input Devices" completed={!!completed["input"]} onToggle={() => toggle("input")}>
            <p>An input device gets data into the computer. This data is stored in <strong>RAM</strong>.</p>
            <Table headers={["Device", "Key Facts"]} rows={[
              ["Keyboard", "80–110 keys. QWERTY layout. Typing, numeric, function, control keys."],
              ["Mouse", "Pointing device. Optical mice use LED, no mechanical parts."],
              ["Scanner", "Converts images to digital files. OCR converts text images to editable text."],
              ["Barcode scanner", "Reads patterns at POS terminals. Red LED reads black/white bars."],
              ["QR code reader", "2D pattern, stores more than barcodes. Read by smartphone camera."],
              ["Magnetic stripe reader", "Reads encoded data from credit/ATM card magnetic strips."],
              ["Biometric device", "Identifies people by fingerprints, iris scans, or voiceprints."],
              ["Microphone", "Converts sound to electrical/digital energy using a diaphragm."],
              ["Sensor", "Converts physical properties (temp, speed, light) to digital signals."],
            ]} />
            <InfoBox title="Touch Screen" color="orange">A touch screen is <strong>both</strong> an input and output device — it displays information AND accepts finger/stylus input.</InfoBox>
            <div className="mt-3 font-semibold text-slate-700">Quick check:</div>
            <p className="mt-1">What is the difference between a barcode and a QR code?</p>
            <Spoiler>A barcode is a one-dimensional pattern of black and white lines. A QR code is two-dimensional (squares within squares) and can store much more information. QR codes are read by smartphone cameras; barcodes use dedicated scanners.</Spoiler>
          </Section>

          <Section id="output" title="3.5 Output Devices & Ports" completed={!!completed["output"]} onToggle={() => toggle("output")}>
            <p>Output devices receive information from the computer — they do NOT send data to it.</p>
            <Table headers={["Device", "Key Facts"]} rows={[
              ["Monitor (LCD)", "Resolution = pixels (e.g. 1920×1080). Size measured diagonally in inches."],
              ["Printer (Laser/Inkjet)", "Non-impact printers. Laser uses toner; inkjet uses ink."],
              ["3D Printer", "Builds objects layer by layer from a .STL design file (slicing process)."],
              ["Speakers", "Convert digital audio to sound waves. Internal amplifier."],
              ["Headphones", "Like speakers but personal. Headsets combine headphones + microphone."],
            ]} />
            <InfoBox title="Ports" color="blue">
              <strong>USB:</strong> Universal port for mouse, keyboard, printers, flash drives. Has replaced many older ports.<br />
              <strong>HDMI:</strong> Transmits both video AND audio digitally. Replaced VGA and sound jack.<br />
              Most modern computers only have USB and HDMI ports, plus sometimes RJ45 (network cable).
            </InfoBox>
          </Section>

          {/* ===== LU4: SYSTEM SOFTWARE ===== */}

          <Section id="sys-sw" title="4.1 System Software" completed={!!completed["sys-sw"]} onToggle={() => toggle("sys-sw")}>
            <p>System software includes the operating system, utilities, compilers, and drivers.</p>
            <InfoBox title="GUI vs CLI" color="green">
              <strong>GUI:</strong> Graphical — uses Windows, Icons, Mouse, Pointers (WIMP). Easy to use, visual feedback.<br />
              <strong>CLI:</strong> Command Line — text-based, commands typed at a prompt. Powerful but must memorise commands. Over 270 commands available.
            </InfoBox>
            <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">OS Resource Management</h3>
            <p>The OS manages: <strong>I/O</strong> (hiding device details), <strong>file management</strong> (create, save, delete, move), <strong>memory management</strong> (allocating RAM), and <strong>process management</strong> (giving each program CPU time).</p>
            <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Device Drivers</h3>
            <p>Every hardware device needs a <strong>driver</strong> — a small program that tells the OS how to communicate with that device. More up-to-date drivers work better.</p>
            <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Source Code → Executable Code</h3>
            <p>Source code (.java) is compiled into executable code (.class). A Java .class file needs the <strong>JRE</strong> (Java Runtime Environment) to run.</p>
            <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Utilities</h3>
            <p><strong>Defragmentation</strong> rearranges fragmented data on an HDD. Not needed for SSDs (no moving parts). Other utilities: task scheduler, backup, firewall, antivirus.</p>
            <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Programming Tools</h3>
            <p>An <strong>IDE</strong> (Integrated Development Environment) includes a source code editor, compiler, and debugger. E.g. NetBeans supports Java, PHP, C++.</p>
          </Section>

          <Section id="licensing" title="4.2 Software Licensing" completed={!!completed["licensing"]} onToggle={() => toggle("licensing")}>
            <p>A <strong>software licence</strong> legally describes how software can be used. It protects the <strong>copyright</strong> of the authors.</p>
            <Table headers={["Licence Type", "Key Features"]} rows={[
              ["Proprietary", "Cannot freely distribute/copy. Need activation key. No source code. E.g. Microsoft Office."],
              ["Open Source (GPL)", "Source code shared. Can modify and redistribute. Copyleft. E.g. Linux."],
              ["Creative Commons", "Can copy/distribute if you acknowledge the creator."],
              ["Freeware", "Free to use, but copyrighted. Cannot alter or resell. No source code."],
              ["Freemium", "Basic features free. Pay for advanced features (licence key)."],
            ]} />
            <InfoBox title="Key Terms" color="orange">
              <strong>Piracy</strong> = illegally copying software.<br />
              <strong>EULA</strong> = End User Licence Agreement (e.g. Microsoft).<br />
              <strong>License manager</strong> = software that enforces licence terms.
            </InfoBox>
          </Section>

          {/* ===== PRACTICE EXERCISES ===== */}

          <Section id="exercises" title="Practice Exercises (Mixed)" completed={!!completed["exercises"]} onToggle={() => toggle("exercises")}>
            <InfoBox title="Instructions" color="blue">Work through each on paper first, then check your answer.</InfoBox>
            <div className="space-y-5 mt-4">
              <div>
                <p className="font-semibold">Exercise 1 — Binary ↔ Decimal</p>
                <p className="text-sm ml-4 mt-1">a) Convert 10110₂ to decimal.</p><Spoiler label="Answer">16+0+4+2+0 = 22</Spoiler>
                <p className="text-sm ml-4">b) Convert 200₁₀ to binary.</p><Spoiler label="Answer">200 = 128+64+8 = 11001000₂</Spoiler>
                <p className="text-sm ml-4">c) Convert 11111111₂ to decimal.</p><Spoiler label="Answer">128+64+32+16+8+4+2+1 = 255</Spoiler>
              </div>
              <div>
                <p className="font-semibold">Exercise 2 — Hexadecimal</p>
                <p className="text-sm ml-4 mt-1">a) Convert 140₁₀ to hexadecimal.</p><Spoiler label="Answer">{`140÷16=8 r12(C) → 8÷16=0 r8\nAnswer: 8C₁₆`}</Spoiler>
                <p className="text-sm ml-4">b) Convert FACE₁₆ to decimal.</p><Spoiler label="Answer">{`15×16³ + 10×16² + 12×16¹ + 14×16⁰\n= 61440 + 2560 + 192 + 14 = 64206`}</Spoiler>
                <p className="text-sm ml-4">c) Convert 10011110₂ directly to hexadecimal.</p><Spoiler label="Answer">{`1001 1110 → 9 E → 9E₁₆`}</Spoiler>
              </div>
              <div>
                <p className="font-semibold">Exercise 3 — Min/Max Values</p>
                <p className="text-sm ml-4 mt-1">a) 10 unsigned bits: range?</p><Spoiler label="Answer">0 to 2¹⁰ − 1 = 0 to 1,023</Spoiler>
                <p className="text-sm ml-4">b) 10 signed bits: range?</p><Spoiler label="Answer">−2⁹ to 2⁹ − 1 = −512 to 511</Spoiler>
              </div>
              <div>
                <p className="font-semibold">Exercise 4 — Screen Memory</p>
                <p className="text-sm mt-1">Calculate memory per second for a 360×640 screen at 150Hz using RGB (3 bytes/pixel).</p>
                <Spoiler>{`360 × 640 = 230,400 pixels\n× 3 bytes = 691,200 bytes per frame\n× 150 Hz = 103,680,000 bytes/sec\n≈ 103.7 MB per second`}</Spoiler>
              </div>
              <div>
                <p className="font-semibold">Exercise 5 — Definitions</p>
                <p className="text-sm ml-4 mt-1">a) The collection of physical components of a computer system.</p><Spoiler label="Answer">Hardware</Spoiler>
                <p className="text-sm ml-4">b) Raw, unprocessed facts.</p><Spoiler label="Answer">Data</Spoiler>
                <p className="text-sm ml-4">c) Memory that loses its contents when power is off.</p><Spoiler label="Answer">Volatile memory (e.g. RAM)</Spoiler>
                <p className="text-sm ml-4">d) A small program allowing the OS to communicate with hardware.</p><Spoiler label="Answer">Device driver</Spoiler>
                <p className="text-sm ml-4">e) Software given away free but cannot be altered.</p><Spoiler label="Answer">Freeware</Spoiler>
                <p className="text-sm ml-4">f) A device that converts sound energy into digital energy.</p><Spoiler label="Answer">Microphone</Spoiler>
                <p className="text-sm ml-4">g) The core of the OS that is always in memory.</p><Spoiler label="Answer">Kernel</Spoiler>
                <p className="text-sm ml-4">h) Technology for identifying people by physical characteristics.</p><Spoiler label="Answer">Biometrics</Spoiler>
              </div>
              <div>
                <p className="font-semibold">Exercise 6 — Compare</p>
                <p className="text-sm mt-1">Give ONE advantage of an HDD over an SSD, and ONE advantage of an SSD over an HDD.</p>
                <Spoiler>{`HDD advantage: Higher storage capacity per cost (cheaper per GB).\nSSD advantage: Much faster access times / no moving parts (more durable) / less power / silent.`}</Spoiler>
              </div>
              <div>
                <p className="font-semibold">Exercise 7 — Volatile vs Non-volatile</p>
                <p className="text-sm mt-1">Explain the difference. Give one example of each.</p>
                <Spoiler>{`Volatile: Loses contents when power is off. Example: RAM.\nNon-volatile: Retains contents when power is off. Example: ROM / SSD / HDD / Flash drive.`}</Spoiler>
              </div>
              <div>
                <p className="font-semibold">Exercise 8 — Stand-alone vs Network</p>
                <p className="text-sm mt-1">A banking app on your phone connects to the bank's server. Is this a stand-alone or network application? Explain.</p>
                <Spoiler>{`Network application. It connects to a remote server over the internet to access and share data (account balances, transactions). Multiple users access the same server.`}</Spoiler>
              </div>
              <div>
                <p className="font-semibold">Exercise 9 — GUI vs CLI</p>
                <p className="text-sm mt-1">Give TWO reasons why a GUI is easier to use than a CLI.</p>
                <Spoiler>{`1. Uses visual elements (windows, icons, menus) — no need to memorise commands.\n2. Uses a mouse/pointing device to click and select — more intuitive.`}</Spoiler>
              </div>
              <div>
                <p className="font-semibold">Exercise 10 — Processing Cycle</p>
                <p className="text-sm mt-1">A user types a document in Microsoft Word and saves it. Identify the input, processing, output, and storage in this scenario.</p>
                <Spoiler>{`Input: Keyboard (typing text)\nProcessing: CPU processes keystrokes, spell-check runs\nOutput: Text displayed on the monitor\nStorage: Document saved to secondary storage (HDD/SSD)`}</Spoiler>
              </div>
              <div>
                <p className="font-semibold">Exercise 11 — Licensing</p>
                <p className="text-sm mt-1">What type of licence does Linux use? Give TWO features of this licence type.</p>
                <Spoiler>{`Open source licence (GPL / General Public License).\n1. Source code is shared — users can view, modify, and redistribute.\n2. Uses copyleft — modified versions must also be open source.`}</Spoiler>
              </div>
              <div>
                <p className="font-semibold">Exercise 12 — Barcode vs QR Code</p>
                <p className="text-sm mt-1">How does a QR code differ from a barcode?</p>
                <Spoiler>{`A barcode is one-dimensional (lines). A QR code is two-dimensional (squares).\nQR codes can store much more information than barcodes.\nQR codes can be read by a smartphone camera; barcodes need a dedicated scanner.`}</Spoiler>
              </div>
            </div>
          </Section>

          <div className="mt-12 pt-6 border-t border-amber-200 text-center text-xs text-slate-500">
            <p>Grade 10 Information Technology · Data Representation · Hardware · System Software</p>
            <p className="mt-1">Helpmekaar Kollege · IEB Affiliated · 2026</p>
          </div>

          </div>
        )}
      </main>
      </div>

      {showTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full bg-amber-600 text-white shadow-lg flex items-center justify-center hover:bg-amber-700 transition-colors">
          <ArrowUp size={18} />
        </button>
      )}
    </div>
  );
}
